import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { CheckCircle2, XCircle, Loader2, Mail } from 'lucide-react';
import { ImageWithFallback } from '../components/ImageWithFallback';
import orbitLogo from '/images/logo.png';
import orbitDarkLogo from '/images/logo-dark.png';
import axios from 'axios';
import { useTheme } from 'next-themes';

interface VerifyEmailProps {
  onNavigateToLogin?: () => void;
}

export function VerifyEmail({ onNavigateToLogin }: VerifyEmailProps) {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const { theme } = useTheme();
  const [status, setStatus] = useState<'verifying' | 'success' | 'error'>('verifying');
  const [message, setMessage] = useState('');
  const [isResending, setIsResending] = useState(false);

  useEffect(() => {
    if (token) {
      verifyEmail(token);
    } else {
      setStatus('error');
      setMessage('Invalid verification link');
    }
  }, [token]);

  const verifyEmail = async (verificationToken: string) => {
    try {
      const response = await axios.get(
        `${import.meta.env.VITE_API_URL}/auth/verify-email/${verificationToken}`
      );

      const alreadyVerified = response.data?.data?.alreadyVerified === true;

      setStatus('success');
      setMessage(alreadyVerified ? 'Email is already verified' : 'Email verified successfully!');
      toast.success(alreadyVerified ? 'Your email is already verified. You can log in.' : 'Email verified successfully! You can now log in.');
      
      setTimeout(() => {
        if (onNavigateToLogin) {
          onNavigateToLogin();
        } else {
          navigate('/login');
        }
      }, 3000);
      
    } catch (error: any) {
      setStatus('error');
      const errorMessage = error.response?.data?.error || 
                          error.response?.data?.message || 
                          'Verification failed. The link may have expired.';
      setMessage(errorMessage);
      toast.error(errorMessage);
    }
  };

  const handleResendVerification = async () => {
    const email = prompt('Please enter your email address to resend verification:');
    
    if (!email) return;

    setIsResending(true);
    try {
      await axios.post(
        `${import.meta.env.VITE_API_URL}/auth/resend-verification`,
        { emailid: email }
      );
      
      toast.success('Verification email sent! Please check your inbox.');
    } catch (error: any) {
      const errorMessage = error.response?.data?.error || 
                          error.response?.data?.message || 
                          'Failed to resend verification email.';
      toast.error(errorMessage);
    } finally {
      setIsResending(false);
    }
  };

  const handleGoToLogin = () => {
    if (onNavigateToLogin) {
      onNavigateToLogin();
    } else {
      navigate('/login');
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-8 bg-background">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <img 
            src={theme === 'dark' ? orbitDarkLogo : orbitLogo} 
            alt="ORbiT8" 
            className="h-12 w-auto mx-auto mb-4"
          />
        </div>

        <Card className="bg-card shadow-lg rounded-lg border">
          <CardHeader className="space-y-1 pb-6 text-center">
            <div className="flex justify-center mb-4">
              {status === 'verifying' && (
                <Loader2 className="w-16 h-16 text-primary animate-spin" />
              )}
              {status === 'success' && (
                <CheckCircle2 className="w-16 h-16 text-green-500" />
              )}
              {status === 'error' && (
                <XCircle className="w-16 h-16 text-red-500" />
              )}
            </div>
            
            <CardTitle className="text-2xl">
              {status === 'verifying' && 'Verifying Email...'}
              {status === 'success' && 'Email Verified!'}
              {status === 'error' && 'Verification Failed'}
            </CardTitle>
            
            <CardDescription className="text-center">
              {status === 'verifying' && 'Please wait while we verify your email address'}
              {status === 'success' && 'Your email has been successfully verified'}
              {status === 'error' && 'We couldn\'t verify your email address'}
            </CardDescription>
          </CardHeader>
          
          <CardContent className="space-y-6">
            {message && (
              <div className={`p-4 rounded-lg text-center ${
                status === 'success' 
                  ? 'bg-green-50 dark:bg-green-950 text-green-800 dark:text-green-200 border border-green-200 dark:border-green-800' 
                  : status === 'error'
                  ? 'bg-red-50 dark:bg-red-950 text-red-800 dark:text-red-200 border border-red-200 dark:border-red-800'
                  : 'bg-blue-50 dark:bg-blue-950 text-blue-800 dark:text-blue-200 border border-blue-200 dark:border-blue-800'
              }`}>
                {message}
              </div>
            )}

            {status === 'success' && (
              <div className="space-y-4">
                <p className="text-sm text-center text-muted-foreground">
                  Redirecting to login in 3 seconds...
                </p>
                <Button
                  onClick={handleGoToLogin}
                  className="w-full"
                >
                  Go to Login Now
                </Button>
              </div>
            )}

            {status === 'error' && (
              <div className="space-y-4">
                <div className="text-sm text-center text-muted-foreground">
                  <p className="mb-2">Your verification link may have expired.</p>
                  <p>Click below to request a new verification email.</p>
                </div>
                
                <Button
                  onClick={handleResendVerification}
                  disabled={isResending}
                  variant="outline"
                  className="w-full"
                >
                  {isResending ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Sending...
                    </>
                  ) : (
                    <>
                      <Mail className="w-4 h-4 mr-2" />
                      Resend Verification Email
                    </>
                  )}
                </Button>

                <Button
                  onClick={handleGoToLogin}
                  variant="ghost"
                  className="w-full"
                >
                  Back to Login
                </Button>
              </div>
            )}

            {status === 'verifying' && (
              <div className="text-sm text-center text-muted-foreground">
                This should only take a moment...
              </div>
            )}
          </CardContent>
        </Card>

        <div className="text-center mt-4">
          <p className="text-sm text-muted-foreground">
            Need help?{' '}
            <a 
              href="mailto:orbit8@sourcecodecontrol.com" 
              className="text-primary hover:text-primary/80 hover:underline"
            >
              Contact Support
            </a>
          </p>
        </div>
      </div>
    </div>
  );
}
